import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Link,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EventIcon from "@mui/icons-material/Event";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RefreshIcon from "@mui/icons-material/Refresh";
import StatusChip, { SeverityChip } from "../common/StatusChip";
import Timeline from "./Timeline";
import DryRunPanel from "./DryRunPanel";
import ArtifactsPanel from "./ArtifactsPanel";
import StatusProgressStepper from "./StatusProgressStepper";
import ConfirmDialog from "../common/ConfirmDialog";
import PlaybookSummary from "../RequestFixForm/PlaybookSummary";
import {
  approveRequests,
  cancelRequests,
  fetchRequestDetail,
  fetchCheckPlaybook,
  refreshRequests,
  runNowRequests,
} from "../../../../store/remediation/remediationSlice";
import { friendlyErrorMessage, friendlySkippedReason, formatErrorCodeLine } from "../common/ErrorCodeMap";
import { useRemediationSnackbar } from "../common/SnackbarHost";
import { formatAbsolute, formatRelative, useActionFeedback, useFetchErrorCodesOnce } from "../common/hooks";
import { NON_ACTIONABLE_STATUSES } from "../common/statusColors";
import {
  hubTabsSx,
  hubTabTrackSx,
  remediationHubSx,
  HUB_TEXT,
  HUB_TRACK,
} from "../common/filterBarStyles";

const APPROVE_ACK =
  "I understand that approving these requests will schedule automated changes to my AWS infrastructure.";
const RUN_NOW_CONFIRM = "REMEDIATE NOW";

/** POST /remediation/requests/refresh — stale preview on Awaiting Approval. */
const REFRESHABLE_STATUSES = new Set(["NEEDS_INPUTS", "DRY_RUN_READY"]);

const REQUEST_KIND_LABELS = {
  auto_approved: "Auto-Approved",
  run_now: "Run Now",
  scheduled: "Scheduled",
  asap: "ASAP",
};

const FAILED_DETAIL_STATUSES = new Set(["FAILED", "PRECHECK_FAILED", "VERIFY_FAILED"]);

const TABS = ["overview", "artifacts", "timeline"];

/** Display helper for RemediationRequestDetail.inputs_rendered entries. */
const formatRenderedInputValue = (entry) => {
  if (entry?.missing) {
    return entry.required ? "Required value missing" : "Not provided";
  }
  const value = entry?.value;
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const RequestDetailDrawer = ({ open, requestId, onClose, onRequestRefetch, onReschedule, onGoToIamTab }) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useRemediationSnackbar();
  useFetchErrorCodesOnce();
  const detail = useSelector((s) =>
    requestId != null ? s.remediation.detailById[requestId] : null
  );
  const detailStatus = useSelector((s) => s.remediation.detailStatus);
  const checkPlaybooks = useSelector((s) => s.remediation.checkPlaybooks);
  const errorCatalog = useSelector((s) => s.remediation?.errorCodes?.byCode) || {};
  const actionInProgress = useSelector((s) => s.remediation.actionInProgress);
  const [activeTab, setActiveTab] = useState("overview");
  const [confirm, setConfirm] = useState(null); // null | "approve" | "cancel" | "run-now"
  const submitting = Boolean(
    actionInProgress?.approve ||
    actionInProgress?.cancel ||
    actionInProgress?.["run-now"] ||
    actionInProgress?.refresh
  );
  const loading = detailStatus?.status === "loading" && detailStatus?.id === requestId;

  const load = () => {
    if (requestId == null) return;
    dispatch(fetchRequestDetail(requestId));
  };

  useEffect(() => {
    if (open && requestId != null) {
      setActiveTab("overview");
      dispatch(fetchRequestDetail(requestId));
    }
  }, [open, requestId, dispatch]);

  useEffect(() => {
    if (open && detail?.check_id) {
      dispatch(fetchCheckPlaybook(detail.check_id));
    }
  }, [open, detail?.check_id, dispatch]);

  const status = detail?.status;
  const canApprove = status === "DRY_RUN_READY";
  const canCancel = status && !NON_ACTIONABLE_STATUSES.has(status);
  const canRunNow = status === "APPROVED";
  const canReschedule = status && !NON_ACTIONABLE_STATUSES.has(status);
  const canRefresh = REFRESHABLE_STATUSES.has(status);
  const playbook = detail?.check_id ? checkPlaybooks[detail.check_id] : null;
  const missingPermissions = detail?.preview_summary?.missing_permissions;
  const failureNote = (() => {
    if (!FAILED_DETAIL_STATUSES.has(status) || !Array.isArray(detail?.timeline)) return null;
    for (let i = detail.timeline.length - 1; i >= 0; i -= 1) {
      const ev = detail.timeline[i];
      const code = ev.code || ev.error_code;
      const catalogEntry = typeof code === "string" ? errorCatalog[code] : null;
      const line = formatErrorCodeLine(code, catalogEntry) || ev.message || ev.note;
      if (line) return line;
    }
    return null;
  })();

  // Friendly past-tense verb per action kind for the success toast.
  const verbByKind = {
    approve: "Approved",
    cancel: "Cancelled",
    "run-now": "Run-now scheduled for",
    refresh: "Refreshed preview for",
  };

  const dispatchAction = (kind, body) => {
    if (kind === "approve") dispatch(approveRequests(body));
    else if (kind === "cancel") dispatch(cancelRequests(body));
    else if (kind === "run-now") dispatch(runNowRequests(body));
    else if (kind === "refresh") dispatch(refreshRequests(body));
  };

  useActionFeedback(
    ["approve", "cancel", "run-now", "refresh"],
    ({ kind, succeeded = [], skipped = [], error }) => {
      if (!open) return;
      const verb = verbByKind[kind] || kind;
      if (error) {
        showSnackbar({ message: friendlyErrorMessage(error), severity: "error" });
        return;
      }
      if (succeeded.length && skipped.length === 0) {
        showSnackbar({
          message: `${verb} ${succeeded.length === 1 ? "task" : `${succeeded.length} tasks`}.`,
          severity: "success",
        });
      } else if (succeeded.length && skipped.length) {
        showSnackbar({
          message: `${verb} ${succeeded.length}, skipped ${skipped.length}.`,
          severity: "warning",
          title: "Partial success",
          details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
        });
      } else {
        showSnackbar({
          message: "No tasks were updated.",
          severity: "warning",
          title: "All skipped",
          details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
        });
      }
      setConfirm(null);
      load();
      if (typeof onRequestRefetch === "function") onRequestRefetch();
    }
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", md: 640 },
            display: "flex",
            flexDirection: "column",
            ...remediationHubSx,
          },
        }}
      >
        {(!detail || loading) && requestId != null && (
          <Box sx={{ p: 3, pb: 6, overflow: "auto", flex: 1 }}>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rectangular" height={140} sx={{ mt: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
          </Box>
        )}
        {detail && (
          <Box sx={{ p: 3, pb: 6, overflow: "auto", flex: 1 }}>
            {/* Header */}
            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Task #{detail.id} · {detail.account_name || detail.account_number}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: HUB_TEXT, fontSize: "1.125rem", lineHeight: 1.35 }}>
                  {detail.check_title}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                  {detail.resource_arn}
                </Typography>
              </Box>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
              <StatusChip status={detail.status} />
              <SeverityChip severity={detail.severity} />
              {REQUEST_KIND_LABELS[detail.request_kind] && (
                <Chip
                  size="small"
                  label={REQUEST_KIND_LABELS[detail.request_kind]}
                  sx={{ backgroundColor: HUB_TRACK }}
                />
              )}
              <Chip
                size="small"
                label={`Status entered ${formatRelative(detail.status_entered_at)}`}
                sx={{ backgroundColor: HUB_TRACK }}
              />
              {detail.scheduled_at && (
                <Chip
                  size="small"
                  icon={<EventIcon fontSize="small" />}
                  label={`Scheduled ${formatAbsolute(detail.scheduled_at)}`}
                  sx={{ backgroundColor: HUB_TRACK }}
                />
              )}
            </Stack>

            <StatusProgressStepper
              status={detail.status}
              timeline={detail.timeline}
              createdAt={detail.created_at}
              dryRunReadyAt={detail.dry_run_ready_at}
              statusEnteredAt={detail.status_entered_at}
            />

            {/* Action bar */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={<CheckCircleIcon />}
                onClick={() => setConfirm("approve")}
                disabled={!canApprove || submitting}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<PlayArrowIcon />}
                onClick={() => setConfirm("run-now")}
                disabled={!canRunNow || submitting}
              >
                Run now
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EventIcon />}
                onClick={() => onReschedule && onReschedule(detail.id)}
                disabled={!canReschedule || submitting}
              >
                Re-schedule
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setConfirm("cancel")}
                disabled={!canCancel || submitting}
              >
                Cancel
              </Button>
              {canRefresh && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => dispatchAction("refresh", { request_ids: [detail.id] })}
                  disabled={submitting}
                >
                  Refresh preview
                </Button>
              )}
              {detail.status === "NEEDS_INPUTS" && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditNoteIcon />}
                  onClick={() => {
                    // For now, route to the request form to re-supply inputs.
                    onClose && onClose();
                  }}
                  disabled={submitting}
                >
                  Provide inputs
                </Button>
              )}
            </Stack>

            {detail.status === "PERMS_DENIED" && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                CloudCatcher&apos;s role is missing one or more IAM actions this fix needs.
                {Array.isArray(missingPermissions) && missingPermissions.length > 0 ? (
                  <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2.5 }}>
                    {missingPermissions.map((p, i) => (
                      <li key={i}><code>{p}</code></li>
                    ))}
                  </Box>
                ) : (
                  " "
                )}
                Add {Array.isArray(missingPermissions) && missingPermissions.length > 0 ? "the listed action(s)" : "the required action(s)"} to the CloudCatcher IAM stack in your AWS account — edit the
                stack or re-deploy the template{" "}
                {onGoToIamTab ? (
                  <Link
                    component="button"
                    type="button"
                    underline="always"
                    onClick={() => {
                      onGoToIamTab();
                      onClose?.();
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: "inherit",
                      verticalAlign: "baseline",
                      border: "none",
                      background: "none",
                      padding: 0,
                      cursor: "pointer",
                      color: "inherit",
                    }}
                  >
                    here
                  </Link>
                ) : (
                  "on the IAM Stack tab"
                )}
                . CloudCatcher rechecks Permission Denied tasks on a regular cycle. Once the permission
                lands, the task moves forward automatically — you do not need to create a new task or
                click Retry.
              </Alert>
            )}
            {failureNote && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {failureNote}
              </Alert>
            )}
            {detail.cancellation_reason && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Cancellation reason:</strong> {detail.cancellation_reason}
              </Alert>
            )}

            <Box sx={{ ...hubTabTrackSx, mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={hubTabsSx}
            >
              <Tab value="overview" label="Overview" />
              <Tab value="artifacts" label="Artifacts" />
              <Tab value="timeline" label={`Timeline (${detail.timeline?.length || 0})`} />
            </Tabs>
            </Box>

            {activeTab === "overview" && (
              <Box>
                {detail.preview_summary ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
                      Validation plan
                    </Typography>
                    <DryRunPanel preview={detail.preview_summary} />
                  </Box>
                ) : null}

                {playbook ? (
                  <Box sx={{ mb: 2 }}>
                    <PlaybookSummary playbook={playbook} />
                  </Box>
                ) : null}

                <Typography variant="caption" sx={{ fontWeight: 600 }}>Inputs you provide</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {Array.isArray(detail.inputs_rendered) && detail.inputs_rendered.length > 0 ? (
                    <Stack spacing={1.25}>
                      {detail.inputs_rendered.map((entry, idx) => (
                        <Box key={entry.name || idx}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                            {entry.label || entry.name || "Input"}
                            {entry.required ? (
                              <Box component="span" sx={{ color: "error.main" }}> *</Box>
                            ) : null}
                          </Typography>
                          {entry.description ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {entry.description}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: entry.missing && entry.required ? "error.main" : "text.primary",
                              fontFamily: typeof entry.value === "object" ? "monospace" : undefined,
                              wordBreak: "break-word",
                            }}
                          >
                            {formatRenderedInputValue(entry)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">No inputs rendered.</Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Created</Typography>
                    <Typography variant="caption">{formatAbsolute(detail.created_at)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Created by</Typography>
                    <Typography variant="caption">{detail.created_by || detail.user_email}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Last modified</Typography>
                    <Typography variant="caption">{formatRelative(detail.last_modified_at)}</Typography>
                  </Box>
                  {detail.dormancy_last_nudged_at && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Last nudge</Typography>
                      <Typography variant="caption">{formatRelative(detail.dormancy_last_nudged_at)}</Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            {activeTab === "artifacts" && (
              <ArtifactsPanel requestId={detail.id} active={activeTab === "artifacts"} />
            )}

            {activeTab === "timeline" && (
              <Timeline
                events={detail.timeline}
                requestKind={detail.request_kind}
                taskTypeLabel={REQUEST_KIND_LABELS[detail.request_kind]}
              />
            )}
          </Box>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirm === "approve"}
        title="Approve remediation"
        description="Approving moves this task to Scheduled and queues execution."
        mode="checkbox"
        requiredText={APPROVE_ACK}
        confirmLabel="Approve"
        confirmColor="primary"
        preview={detail?.preview_summary ? <DryRunPanel preview={detail.preview_summary} /> : null}
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          dispatchAction("approve", { request_ids: [detail.id], acknowledgement: APPROVE_ACK })
        }
        isSubmitting={submitting}
      />
      <ConfirmDialog
        open={confirm === "cancel"}
        title="Cancel remediation"
        description="Provide an optional reason — your team will see it on the task timeline."
        mode="reason"
        confirmLabel="Cancel task"
        confirmColor="error"
        onClose={() => setConfirm(null)}
        onConfirm={({ reason }) =>
          dispatchAction("cancel", { request_ids: [detail.id], reason: reason || null })
        }
        isSubmitting={submitting}
      />
      <ConfirmDialog
        open={confirm === "run-now"}
        title="Run remediation immediately"
        description="Bypasses the scheduled time and runs on the next task cycle."
        mode="match"
        requiredText={RUN_NOW_CONFIRM}
        confirmLabel="Run now"
        confirmColor="warning"
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          dispatchAction("run-now", { request_ids: [detail.id], confirmation: RUN_NOW_CONFIRM })
        }
        isSubmitting={submitting}
      />
    </>
  );
};

export default RequestDetailDrawer;
